/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { Modal, message, Radio, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ActionGetOrderByID, ActionCreatePaymentLink } from '../../store/Actions/OrderHistoryAction';
import { GetCustomerDashboard } from '../../store/Actions/PatientAction';
import { ActionCheckOut } from '../../store/Actions/MasterDataAction';
import { ActionCreateByUrl } from '../../store/Actions/CommonAction';
import { useFormik } from 'formik';
import { collectCashSchema } from '../../utils/FormValidation';
import { InputFromUser } from '../../components/Input/input';
import { calculateBilling, rowPaidAmount } from '../../utils/billingUtils';
import { calculatePercentage } from '../../utils/functions';
import './Checkout.css';

const Checkout = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { orderId, bookingId } = useParams();
    const [loading, setLoading] = useState(true);
    const [checkingOut, setCheckingOut] = useState(false);
    const [activeTab, setActiveTab] = useState('booking');

    const { orderDetail: order, loader: orderLoader } = useSelector((state) => state?.OrderHistory) || {};
    const { CustomerDashbord: patientData, loader: patientLoader } = useSelector((state) => state?.Patient) || {};
    const { userDetails } = useSelector((state) => state?.auth);

    // Modal State
    const [IsCollectModal, setCollectModal] = useState(null);
    const [paymentLinkType, setPaymentLinkType] = useState('Cash'); // 'Cash', 'Email Link', 'WhatsApp Link'
    const [paymentLinkAmount, setPaymentLinkAmount] = useState('');
    const [Isloading, setIsloading] = useState(false);
    const [remark, setRemark] = useState('');

    // Derived values for Modal using centralized billing logic
    const modalBillingInfo = calculateBilling(IsCollectModal);
    const modalDiscount = modalBillingInfo?.discountValue || 0;
    const modalPayableAmount = modalBillingInfo?.netAmount || 0;
    const modalPendingAmt = modalBillingInfo?.pending || 0;

    // Formik for Cash Collection
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            updatedPaidAmount: modalPendingAmt || '',
        },
        validationSchema: collectCashSchema,
        onSubmit: (values, { setErrors }) => {
            const currentPaidTotal = rowPaidAmount(IsCollectModal);
            const collectionAmount = parseFloat(values.updatedPaidAmount || 0);
            const newTotalPaid = currentPaidTotal + collectionAmount;

            const billing = calculateBilling(IsCollectModal);
            const netPayable = billing?.netAmount || 0;

            const updatedStatus = newTotalPaid >= (netPayable - 1) ? 'Completed' : 'Pending';
            const Percentage = calculatePercentage(netPayable, newTotalPaid);

            const payload = {
                updatedBy: userDetails?.[0]?._id,
                updateByName: userDetails?.[0]?.name,
                paymentMode: 'Cash',
                updatedPaidAmount: newTotalPaid,
                paidAmount: collectionAmount,
                completedPayment: `${Percentage}%`,
                paymentStatus: updatedStatus,
                remark: remark,
            };

            setIsloading(true);
            dispatch(
                ActionCreateByUrl(
                    { Query: `admin/order/order_update/${IsCollectModal?.orderId || IsCollectModal?.id}`, obj: payload },
                    (Res) => {
                        setIsloading(false);
                        if (Res?.statusCode === 200) {
                            message.success('Payment collected successfully');
                            setCollectModal(null);
                            setRemark('');
                            // Refresh order details after payment
                            if (orderId) {
                                dispatch(ActionGetOrderByID({ Query: `/${orderId}` }));
                            }
                        } else if (Res?.statusCode === 400) {
                            setErrors(Res?.data?.errors);
                            message.error(Res?.message || 'Failed to update payment');
                        }
                    }
                )
            );
        },
    });

    const handlePaymentLinkSubmit = () => {
        if (paymentLinkType === 'Email Link' || paymentLinkType === 'WhatsApp Link') {
            if (!paymentLinkAmount || paymentLinkAmount <= 0) {
                message.error('Please enter a valid amount');
                return;
            }
            if (paymentLinkAmount > modalPendingAmt) {
                message.error(`Amount cannot exceed pending amount of ₹${modalPendingAmt}`);
                return;
            }
            setIsloading(true);
            const payload = {
                amount: paymentLinkAmount,
                orderId: IsCollectModal?.id || IsCollectModal?._id,
                bookingFor: IsCollectModal?.bookingFor,
                totalAmount: IsCollectModal?.amount,
                treatment: IsCollectModal?.additionalInfo?.treatment,
                isEmail: paymentLinkType === 'Email Link',
                user: {
                    name: IsCollectModal?.additionalInfo?.customerName || IsCollectModal?.additionalInfo?.name,
                    email: IsCollectModal?.additionalInfo?.email || '',
                    phoneNo: IsCollectModal?.additionalInfo?.phoneNo || '',
                },
                remark: remark,
            };

            dispatch(
                ActionCreatePaymentLink(payload, (res) => {
                    setIsloading(false);
                    if (res?.success) {
                        message.success(`Payment link sent successfully via ${paymentLinkType === 'Email Link' ? 'Email' : 'WhatsApp'}`);
                        setCollectModal(null);
                        setPaymentLinkType('Cash');
                        setRemark('');
                    } else {
                        message.error(res?.message || 'Failed to create payment link');
                    }
                })
            );
        } else {
            formik.handleSubmit();
        }
    };

    useEffect(() => {
        if (orderId) {
            dispatch(ActionGetOrderByID({ Query: `/${orderId}` }));
        }
    }, [orderId, dispatch]);

    // Fetch patient data when order is loaded
    useEffect(() => {
        if (order?.customerId?._id) {
            dispatch(GetCustomerDashboard({ Query: `/${order.customerId._id}` }));
            setLoading(false);
        }
    }, [order, dispatch]);

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
        }).format(amount || 0);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    // Get status badge class
    const getStatusBadgeClass = (status) => {
        const statusLower = status?.toLowerCase();
        if (statusLower === 'completed') return 'badge-success';
        if (statusLower === 'pending' || statusLower === 'initialize') return 'badge-warning';
        if (statusLower === 'failed' || statusLower === 'cancelled') return 'badge-danger';
        return 'badge-info';
    };

    // Calculate age from birthDate
    const calculateAge = (birthDate) => {
        if (!birthDate) return '-';
        const birth = new Date(birthDate);
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    const additionalInfo = order?.additionalInfo || {};
    const customerInfo = order?.customerId || {};
    const roomDetails = additionalInfo?.roomDetails || [];
    const treatmentData = additionalInfo?.treatmentData || [];
    const guestDetails = additionalInfo?.guestDetails || [];

    // Patient dashboard data
    const customer = patientData?.[0]?.customer || {};
    const bookingsHistory = patientData?.[0]?.bookingsHistory?.treatments || [];
    const appointmentHistory = patientData?.[0]?.appointmentHistory || [];
    const membershipDetails = customer?.memberShipDetails?.[0] || {};

    // Find matching booking from patient data
    const currentBooking = bookingsHistory.find(b => b.orderId?.id === orderId) || {};
    const assessmentPlan = currentBooking?.assessmentPlan || {};
    const dischargeForm = currentBooking?.dischargeForm || {};
    const yogaConsultation = currentBooking?.yogaConsultation || {};

    const isLoading = orderLoader || patientLoader || loading;

    // Unified billing calculations for the main order
    const orderBilling = calculateBilling(order);
    const totalPaid = orderBilling?.paid || 0;
    const totalAmount = orderBilling?.total || 0;
    const discountValue = orderBilling?.discountValue || 0;
    const payableAmount = orderBilling?.netAmount || 0;
    const pendingAmount = orderBilling?.pending || 0;
    const isPaymentComplete = orderBilling?.isFullyPaid || (additionalInfo?.completedPayment === '100%');

    // Handle checkout
    const handleCheckOut = () => {
        if (!bookingId) {
            message.error('Booking ID not found');
            return;
        }

        Modal.confirm({
            title: 'Confirm Check Out',
            icon: <FeatherIcon icon="alert-circle" style={{ color: '#2c5aa0' }} />,
            content: 'Are you sure you want to check out this guest? This action will mark the booking as completed.',
            okText: 'Confirm Check Out',
            okType: 'primary',
            cancelText: 'Cancel',
            centered: true,
            onOk() {
                setCheckingOut(true);
                dispatch(ActionCheckOut({ bookingId }, (response) => {
                    setCheckingOut(false);
                    if (response?.success) {
                        message.success('Guest checked out successfully!');
                        navigate('/room-dashboard');
                    } else {
                        message.error(response?.message || 'Failed to check out');
                    }
                }));
            },
        });
    };

    return (
        <>
            <Header />
            <Sidebar />
            <div className="page-wrapper">
                <div className="content">
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/room-dashboard">Room Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <i className="feather-chevron-right">
                                            <FeatherIcon icon="chevron-right" />
                                        </i>
                                    </li>
                                    <li className="breadcrumb-item active">Checkout Details</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="checkout-loading">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : !order ? (
                        <div className="checkout-empty">
                            <FeatherIcon icon="shopping-cart" size={64} />
                            <h3>No Order Found</h3>
                            <p>Please provide a valid order ID to view checkout details.</p>
                        </div>
                    ) : (
                        <div className="checkout-container">
                            {/* Order Header */}
                            <div className="checkout-header">
                                <div className="order-summary-header">
                                    <div className="order-id-section">
                                        <span className="order-label">Order ID</span>
                                        <h2 className="order-number">{order?.orderUniqueId || '-'}</h2>
                                        <span className="order-date">
                                            Placed on {formatDate(order?.createdAt)}
                                        </span>
                                    </div>
                                    <div className="order-status-badges">
                                        <span className={`status-badge ${getStatusBadgeClass(order?.orderStatus)}`}>
                                            <FeatherIcon icon="package" size={14} />
                                            {order?.orderStatus}
                                        </span>
                                        <span className={`status-badge ${getStatusBadgeClass(order?.paymentStatus)}`}>
                                            <FeatherIcon icon="credit-card" size={14} />
                                            {order?.paymentStatus}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Tabs Navigation */}
                            <div className="checkout-tabs">
                                <button
                                    className={`tab-btn ${activeTab === 'booking' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('booking')}
                                >
                                    <FeatherIcon icon="calendar" size={16} />
                                    Booking & Payment
                                </button>
                                {/* <button
                                    className={`tab-btn ${activeTab === 'patient' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('patient')}
                                >
                                    <FeatherIcon icon="user" size={16} />
                                    Patient Info
                                </button> */}
                                {/* <button
                                    className={`tab-btn ${activeTab === 'medical' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('medical')}
                                >
                                    <FeatherIcon icon="activity" size={16} />
                                    Medical Details
                                </button> */}
                            </div>

                            <div className="checkout-grid">
                                {/* Left Column */}
                                <div className="checkout-left-column">
                                    {/* Booking & Payment Tab */}
                                    {activeTab === 'booking' && (
                                        <>
                                            {/* Customer Quick Info */}
                                            <div className="checkout-card customer-card">
                                                <div className="card-header">
                                                    <FeatherIcon icon="user" size={20} />
                                                    <h3>Customer Information</h3>
                                                </div>
                                                <div className="card-content">
                                                    <div className="customer-avatar">
                                                        <div className="avatar-circle">
                                                            {customerInfo?.name?.charAt(0)?.toUpperCase() || 'G'}
                                                        </div>
                                                        <div className="customer-details">
                                                            <h4>{customerInfo?.name || additionalInfo?.name || '-'}</h4>
                                                            <span className="registration-no">
                                                                Reg. No: {customerInfo?.registrationNo || '-'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="info-grid">
                                                        <div className="info-item">
                                                            <FeatherIcon icon="mail" size={16} />
                                                            <div>
                                                                <span className="info-label">Email</span>
                                                                <span className="info-value">{customerInfo?.email || additionalInfo?.email || '-'}</span>
                                                            </div>
                                                        </div>
                                                        <div className="info-item">
                                                            <FeatherIcon icon="phone" size={16} />
                                                            <div>
                                                                <span className="info-label">Phone</span>
                                                                <span className="info-value">{customerInfo?.phoneNo || additionalInfo?.phoneNo || '-'}</span>
                                                            </div>
                                                        </div>
                                                        <div className="info-item">
                                                            <FeatherIcon icon="user" size={16} />
                                                            <div>
                                                                <span className="info-label">Gender</span>
                                                                <span className="info-value">{customerInfo?.gender || additionalInfo?.gender || '-'}</span>
                                                            </div>
                                                        </div>
                                                        <div className="info-item">
                                                            <FeatherIcon icon="calendar" size={16} />
                                                            <div>
                                                                <span className="info-label">Age</span>
                                                                <span className="info-value">
                                                                    {calculateAge(customerInfo?.birthDate || additionalInfo?.birthDate)} years
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Booking Details */}
                                            <div className="checkout-card booking-card">
                                                <div className="card-header">
                                                    <FeatherIcon icon="calendar" size={20} />
                                                    <h3>Booking Details</h3>
                                                    <span className="booking-type-badge">{additionalInfo?.bookingFor || order?.bookingFor || '-'}</span>
                                                </div>
                                                <div className="card-content">
                                                    <div className="booking-dates">
                                                        <div className="date-block">
                                                            <span className="date-label">Check-in</span>
                                                            <span className="date-value">{additionalInfo?.startDate || '-'}</span>
                                                        </div>
                                                        <div className="date-arrow">
                                                            <FeatherIcon icon="arrow-right" size={20} />
                                                            <span className="duration">{additionalInfo?.totalDays || '0'} Days</span>
                                                        </div>
                                                        <div className="date-block">
                                                            <span className="date-label">Check-out</span>
                                                            <span className="date-value">{additionalInfo?.endDate || '-'}</span>
                                                        </div>
                                                    </div>

                                                    {additionalInfo?.serviceName && (
                                                        <div className="service-info">
                                                            <span className="service-label">Service</span>
                                                            <span className="service-name">{additionalInfo?.serviceName}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Treatment Details */}
                                            {treatmentData.length > 0 && (
                                                <div className="checkout-card treatment-card">
                                                    <div className="card-header">
                                                        <FeatherIcon icon="activity" size={20} />
                                                        <h3>Treatment Details</h3>
                                                    </div>
                                                    <div className="card-content">
                                                        <div className="treatment-list">
                                                            {treatmentData.map((treatment, index) => (
                                                                <div key={index} className="treatment-item">
                                                                    <div className="treatment-info">
                                                                        <span className="treatment-name">{treatment?.treatmentName || '-'}</span>
                                                                        <span className="treatment-id">ID: {treatment?.treatmentId || '-'}</span>
                                                                    </div>
                                                                    <div className="treatment-price">
                                                                        <span className="price-label">Per Day</span>
                                                                        <span className="price-value">{formatCurrency(treatment?.perDayPrice)}</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Room Details */}
                                            {/* {roomDetails.length > 0 && (
                                                <div className="checkout-card room-card">
                                                    <div className="card-header">
                                                        <FeatherIcon icon="home" size={20} />
                                                        <h3>Room Details</h3>
                                                    </div>
                                                    <div className="card-content">
                                                        <div className="room-list">
                                                            {roomDetails.map((room, index) => (
                                                                <div key={index} className="room-item">
                                                                    <div className="room-icon">
                                                                        <FeatherIcon icon="home" size={24} />
                                                                    </div>
                                                                    <div className="room-info">
                                                                        <h4>{room?.roomTypeName || '-'}</h4>
                                                                        <div className="room-meta">
                                                                            <span className="room-code">{room?.roomTypeCode || '-'}</span>
                                                                            <span className="room-occupancy">
                                                                                <FeatherIcon icon="users" size={12} />
                                                                                {room?.occupancyMode || '-'}
                                                                            </span>
                                                                            <span className="room-capacity">
                                                                                Max Capacity: {room?.maxCapacity || '-'}
                                                                            </span>
                                                                        </div>
                                                                        <div className="room-guest">
                                                                            Guest: {room?.customerName || '-'} ({room?.gender || '-'})
                                                                        </div>
                                                                    </div>
                                                                    <div className="room-pricing">
                                                                        <div className="price-breakdown">
                                                                            <span>{formatCurrency(room?.pricePerDay)} × {additionalInfo?.totalDays || 1} days</span>
                                                                        </div>
                                                                        <div className="total-price">{formatCurrency(room?.totalPrice)}</div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )} */}
                                        </>
                                    )}

                                    {/* Patient Info Tab */}
                                    {activeTab === 'patient' && (
                                        <>
                                            {/* Detailed Patient Info */}
                                            <div className="checkout-card patient-details-card">
                                                <div className="card-header">
                                                    <FeatherIcon icon="user" size={20} />
                                                    <h3>Patient Details</h3>
                                                </div>
                                                <div className="card-content">
                                                    <div className="patient-info-grid">
                                                        <div className="patient-info-row">
                                                            <span className="label">Full Name</span>
                                                            <span className="value">{customer?.name || customerInfo?.name || '-'}</span>
                                                        </div>
                                                        <div className="patient-info-row">
                                                            <span className="label">Registration No.</span>
                                                            <span className="value">{customer?.registrationNo || customerInfo?.registrationNo || '-'}</span>
                                                        </div>
                                                        <div className="patient-info-row">
                                                            <span className="label">Email</span>
                                                            <span className="value">{customer?.email || customerInfo?.email || '-'}</span>
                                                        </div>
                                                        <div className="patient-info-row">
                                                            <span className="label">Phone</span>
                                                            <span className="value">{customer?.phoneNo || customerInfo?.phoneNo || '-'}</span>
                                                        </div>
                                                        <div className="patient-info-row">
                                                            <span className="label">Date of Birth</span>
                                                            <span className="value">{formatDate(customer?.birthDate || customerInfo?.birthDate)}</span>
                                                        </div>
                                                        <div className="patient-info-row">
                                                            <span className="label">Gender</span>
                                                            <span className="value">{customer?.gender || customerInfo?.gender || '-'}</span>
                                                        </div>
                                                        <div className="patient-info-row full-width">
                                                            <span className="label">Address</span>
                                                            <span className="value">{customer?.address || '-'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Membership Details */}
                                            {customer?.isMemberShip && membershipDetails && (
                                                <div className="checkout-card membership-card">
                                                    <div className="card-header">
                                                        <FeatherIcon icon="award" size={20} />
                                                        <h3>Membership Details</h3>
                                                        <span className="membership-badge">Active</span>
                                                    </div>
                                                    <div className="card-content">
                                                        <div className="membership-info-grid">
                                                            <div className="membership-info-item">
                                                                <span className="label">Membership Type</span>
                                                                <span className="value highlight">{membershipDetails?.memberShipType || '-'}</span>
                                                            </div>
                                                            <div className="membership-info-item">
                                                                <span className="label">Programme</span>
                                                                <span className="value">{membershipDetails?.programme || '-'}</span>
                                                            </div>
                                                            <div className="membership-info-item">
                                                                <span className="label">Room Type</span>
                                                                <span className="value">{membershipDetails?.roomType || '-'}</span>
                                                            </div>
                                                            <div className="membership-info-item">
                                                                <span className="label">Occupancy</span>
                                                                <span className="value">{membershipDetails?.type || '-'}</span>
                                                            </div>
                                                            <div className="membership-info-item">
                                                                <span className="label">Valid From</span>
                                                                <span className="value">{formatDate(membershipDetails?.startDate)}</span>
                                                            </div>
                                                            <div className="membership-info-item">
                                                                <span className="label">Valid Until</span>
                                                                <span className="value">{formatDate(membershipDetails?.endDate)}</span>
                                                            </div>
                                                            <div className="membership-info-item">
                                                                <span className="label">Total Days</span>
                                                                <span className="value highlight">{customer?.totalMemberShipDays || 0} Days</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Appointment History */}
                                            {appointmentHistory.length > 0 && (
                                                <div className="checkout-card history-card">
                                                    <div className="card-header">
                                                        <FeatherIcon icon="clock" size={20} />
                                                        <h3>Appointment History</h3>
                                                        <span className="history-count">{appointmentHistory.length} Appointments</span>
                                                    </div>
                                                    <div className="card-content">
                                                        <div className="history-list">
                                                            {appointmentHistory.slice(0, 5).map((appt, index) => (
                                                                <div key={index} className="history-item">
                                                                    <div className="history-date">
                                                                        <FeatherIcon icon="calendar" size={14} />
                                                                        {appt?.date || '-'}
                                                                    </div>
                                                                    <div className="history-details">
                                                                        <span className="history-treatment">{appt?.treatment || '-'}</span>
                                                                        <span className="history-doctor">{appt?.doctor?.name || '-'}</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* Medical Details Tab */}
                                    {activeTab === 'medical' && (
                                        <>
                                            {/* Assessment Plan */}
                                            {assessmentPlan && Object.keys(assessmentPlan).length > 0 && (
                                                <div className="checkout-card medical-card">
                                                    <div className="card-header">
                                                        <FeatherIcon icon="clipboard" size={20} />
                                                        <h3>Assessment Plan</h3>
                                                    </div>
                                                    <div className="card-content">
                                                        <div className="medical-info-grid">
                                                            {assessmentPlan?.chiefComplaint && (
                                                                <div className="medical-info-row full-width">
                                                                    <span className="label">Chief Complaint</span>
                                                                    <span className="value">{assessmentPlan.chiefComplaint}</span>
                                                                </div>
                                                            )}
                                                            {assessmentPlan?.diagnosis && (
                                                                <div className="medical-info-row full-width">
                                                                    <span className="label">Diagnosis</span>
                                                                    <span className="value">{assessmentPlan.diagnosis}</span>
                                                                </div>
                                                            )}
                                                            {assessmentPlan?.medicalHistory?.length > 0 && (
                                                                <div className="medical-info-row full-width">
                                                                    <span className="label">Medical History</span>
                                                                    <div className="tag-list">
                                                                        {assessmentPlan.medicalHistory.map((item, i) => (
                                                                            <span key={i} className="tag">{item}</span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {assessmentPlan?.addictions?.length > 0 && (
                                                                <div className="medical-info-row full-width">
                                                                    <span className="label">Addictions</span>
                                                                    <div className="tag-list warning">
                                                                        {assessmentPlan.addictions.map((item, i) => (
                                                                            <span key={i} className="tag">{item}</span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Vitals */}
                                                        <div className="vitals-section">
                                                            <h4>Vitals</h4>
                                                            <div className="vitals-grid">
                                                                <div className="vital-item">
                                                                    <span className="vital-label">Pulse</span>
                                                                    <span className="vital-value">{assessmentPlan?.pulse || '-'}</span>
                                                                </div>
                                                                <div className="vital-item">
                                                                    <span className="vital-label">BP</span>
                                                                    <span className="vital-value">{assessmentPlan?.bp || '-'}</span>
                                                                </div>
                                                                <div className="vital-item">
                                                                    <span className="vital-label">Temperature</span>
                                                                    <span className="vital-value">{assessmentPlan?.temperature || '-'}°</span>
                                                                </div>
                                                                <div className="vital-item">
                                                                    <span className="vital-label">SpO2</span>
                                                                    <span className="vital-value">{assessmentPlan?.spo2 || '-'}%</span>
                                                                </div>
                                                                <div className="vital-item">
                                                                    <span className="vital-label">RR</span>
                                                                    <span className="vital-value">{assessmentPlan?.rr || '-'}</span>
                                                                </div>
                                                                <div className="vital-item">
                                                                    <span className="vital-label">Pain Score</span>
                                                                    <span className="vital-value">{assessmentPlan?.painScore || '-'}/10</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Doctor Info */}
                                                        {assessmentPlan?.doctorName && (
                                                            <div className="doctor-signature">
                                                                <span>Assessed by: <strong>{assessmentPlan.doctorName}</strong></span>
                                                                <span>Date: {assessmentPlan?.authDate} {assessmentPlan?.authTime}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Discharge Form */}
                                            {dischargeForm && Object.keys(dischargeForm).length > 0 && (
                                                <div className="checkout-card discharge-card">
                                                    <div className="card-header">
                                                        <FeatherIcon icon="file-text" size={20} />
                                                        <h3>Discharge Summary</h3>
                                                    </div>
                                                    <div className="card-content">
                                                        <div className="discharge-info-grid">
                                                            {dischargeForm?.diagnosis && (
                                                                <div className="discharge-info-row">
                                                                    <span className="label">Diagnosis</span>
                                                                    <span className="value">{dischargeForm.diagnosis}</span>
                                                                </div>
                                                            )}
                                                            {dischargeForm?.admissionComplaints && (
                                                                <div className="discharge-info-row">
                                                                    <span className="label">Admission Complaints</span>
                                                                    <span className="value">{dischargeForm.admissionComplaints}</span>
                                                                </div>
                                                            )}
                                                            {dischargeForm?.dischargeComplaints && (
                                                                <div className="discharge-info-row">
                                                                    <span className="label">Discharge Complaints</span>
                                                                    <span className="value">{dischargeForm.dischargeComplaints}</span>
                                                                </div>
                                                            )}
                                                            {dischargeForm?.medicineAdvised && (
                                                                <div className="discharge-info-row">
                                                                    <span className="label">Medicine Advised</span>
                                                                    <span className="value">{dischargeForm.medicineAdvised}</span>
                                                                </div>
                                                            )}
                                                            {dischargeForm?.Exercise && (
                                                                <div className="discharge-info-row">
                                                                    <span className="label">Exercise</span>
                                                                    <span className="value">{dischargeForm.Exercise}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Yoga Consultation */}
                                            {Object.keys(yogaConsultation).length > 0 && (
                                                <div className="checkout-card yoga-card">
                                                    <div className="card-header">
                                                        <FeatherIcon icon="heart" size={20} />
                                                        <h3>Yoga Consultation</h3>
                                                    </div>
                                                    <div className="card-content">
                                                        {Object.entries(yogaConsultation).map(([date, sessions]) => (
                                                            <div key={date} className="yoga-session">
                                                                <div className="yoga-date">
                                                                    <FeatherIcon icon="calendar" size={14} />
                                                                    {date}
                                                                </div>
                                                                {sessions?.[0] && (
                                                                    <>
                                                                        {sessions[0]?.currentComplaints && (
                                                                            <p className="yoga-complaints">
                                                                                <strong>Complaints:</strong> {sessions[0].currentComplaints}
                                                                            </p>
                                                                        )}
                                                                        {sessions[0]?.exercise?.length > 0 && (
                                                                            <div className="exercise-list">
                                                                                {sessions[0].exercise.map((ex, i) => (
                                                                                    <div key={i} className={`exercise-item ${ex.completed === 'true' ? 'completed' : ''}`}>
                                                                                        <FeatherIcon icon={ex.completed === 'true' ? 'check-circle' : 'circle'} size={14} />
                                                                                        {ex.title}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                        {sessions[0]?.notes && (
                                                                            <p className="yoga-notes">
                                                                                <strong>Notes:</strong> {sessions[0].notes}
                                                                            </p>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Empty State for Medical Tab */}
                                            {(!assessmentPlan || Object.keys(assessmentPlan).length === 0) &&
                                                (!dischargeForm || Object.keys(dischargeForm).length === 0) &&
                                                Object.keys(yogaConsultation).length === 0 && (
                                                    <div className="checkout-card empty-medical">
                                                        <div className="card-content">
                                                            <div className="empty-state">
                                                                <FeatherIcon icon="file-text" size={48} />
                                                                <h4>No Medical Records</h4>
                                                                <p>Medical details will appear here once the patient assessment is completed.</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                        </>
                                    )}
                                </div>

                                {/* Right Column - Payment Summary */}
                                <div className="checkout-right-column">
                                    <div className="checkout-card payment-card sticky-card">
                                        <div className="card-header">
                                            <FeatherIcon icon="credit-card" size={20} />
                                            <h3>Payment Summary</h3>
                                        </div>
                                        <div className="card-content">
                                            {/* Payment Method */}
                                            <div className="payment-method-section">
                                                <span className="section-label">Payment Method</span>
                                                <div className="payment-method">
                                                    <FeatherIcon icon={order?.paymentMode === 'upi' ? 'smartphone' : 'credit-card'} size={20} />
                                                    <span>{order?.paymentMode?.toUpperCase() || order?.bookingPaymentType || '-'}</span>
                                                </div>
                                            </div>

                                            {/* Transaction Details */}
                                            {order?.razorpayPaymentId && (
                                                <div className="transaction-details">
                                                    <div className="transaction-item">
                                                        <span className="transaction-label">Transaction ID</span>
                                                        <span className="transaction-value">{order?.razorpayPaymentId}</span>
                                                    </div>
                                                    {order?.razorpayOrderId && (
                                                        <div className="transaction-item">
                                                            <span className="transaction-label">Razorpay Order ID</span>
                                                            <span className="transaction-value">{order?.razorpayOrderId}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="payment-divider"></div>

                                            {/* Price Breakdown */}
                                            <div className="price-breakdown-section">
                                                {roomDetails.length > 0 && (
                                                    <div className="breakdown-item">
                                                        <span>Room Charges</span>
                                                        <span>{formatCurrency(roomDetails.reduce((sum, room) => sum + (room?.totalPrice || 0), 0))}</span>
                                                    </div>
                                                )}

                                                {treatmentData.length > 0 && (
                                                    <div className="breakdown-item">
                                                        <span>Treatment Charges</span>
                                                        <span>{formatCurrency(treatmentData.reduce((sum, t) => sum + ((t?.perDayPrice || 0) * parseInt(additionalInfo?.totalDays || 1)), 0))}</span>
                                                    </div>
                                                )}

                                                <div className="breakdown-item subtotal">
                                                    <span>Total Charges</span>
                                                    <span>{formatCurrency(totalAmount)}</span>
                                                </div>

                                                {discountValue > 0 && (
                                                    <div className="breakdown-item discount">
                                                        <span>
                                                            <FeatherIcon icon="tag" size={14} />
                                                            Discount {order?.coupon && `(${order?.coupon})`}
                                                            {orderBilling?.discountType === 'percentage' && ` - ${orderBilling?.discountRate}%`}
                                                        </span>
                                                        <span>- {formatCurrency(discountValue)}</span>
                                                    </div>
                                                )}

                                                <div className="payment-divider"></div>

                                                <div className="breakdown-item payable">
                                                    <span>Net Payable</span>
                                                    <span>{formatCurrency(payableAmount)}</span>
                                                </div>

                                                <div className="breakdown-item paid">
                                                    <span>Amount Paid</span>
                                                    <span className="paid-amount">{formatCurrency(totalPaid)}</span>
                                                </div>

                                                {pendingAmount > 0 ? (
                                                    <div className="breakdown-item pending">
                                                        <span>Pending Amount</span>
                                                        <span className="pending-amount">{formatCurrency(pendingAmount)}</span>
                                                    </div>
                                                ) : (
                                                    <div className="breakdown-item success">
                                                        <span>Payment Status</span>
                                                        <span className="status-completed">Fully Paid</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="payment-divider thick"></div>

                                            {/* Total */}
                                            <div className="total-section">
                                                <div className="total-row">
                                                    <span className="total-label">Payable Amount</span>
                                                    <span className="total-value">{formatCurrency(payableAmount)}</span>
                                                </div>
                                                <span className="payment-completed">
                                                    {calculatePercentage(payableAmount, totalPaid)}% Payment Completed
                                                </span>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="payment-actions">
                                                {isPaymentComplete ? (
                                                    <button
                                                        className="btn-checkout"
                                                        onClick={handleCheckOut}
                                                        disabled={checkingOut}
                                                    >
                                                        <FeatherIcon icon="log-out" size={16} />
                                                        {checkingOut ? 'Processing...' : 'Complete Checkout'}
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="btn-collect-cash"
                                                        onClick={() => {
                                                            formik.resetForm();
                                                            setPaymentLinkType('Cash');
                                                            setPaymentLinkAmount('');

                                                            const CollectCashPayload = {
                                                                ...order,
                                                                orderId: order?.id || order?._id,
                                                                updatedBy: userDetails?.[0]?._id,
                                                                updateByName: userDetails?.[0]?.name,
                                                                discount: order?.discount || order?.additionalInfo?.discount || 0
                                                            };
                                                            setCollectModal(CollectCashPayload);
                                                        }}
                                                    >
                                                        <FeatherIcon icon="dollar-sign" size={16} />
                                                        Collect Cash
                                                    </button>
                                                )}
                                                {/* <button className="btn-print" onClick={() => window.print()}>
                                                    <FeatherIcon icon="printer" size={16} />
                                                    Print Invoice
                                                </button> */}
                                                <Link to="/room-dashboard" className="btn-back">
                                                    <FeatherIcon icon="arrow-left" size={16} />
                                                    Back to Dashboard
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <PaymentCollectionModal
                                IsCollectModal={IsCollectModal}
                                setCollectModal={setCollectModal}
                                Isloading={Isloading}
                                handlePaymentLinkSubmit={handlePaymentLinkSubmit}
                                paymentLinkType={paymentLinkType}
                                setPaymentLinkType={setPaymentLinkType}
                                discount={modalDiscount}
                                payableAmount={modalPayableAmount}
                                PendingAmt={modalPendingAmt}
                                formik={formik}
                                paymentLinkAmount={paymentLinkAmount}
                                setPaymentLinkAmount={setPaymentLinkAmount}
                                remark={remark}
                                setRemark={setRemark}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

// ============================================================
// Payment Collection Modal Component
// ============================================================
const PaymentCollectionModal = ({
    IsCollectModal,
    setCollectModal,
    Isloading,
    handlePaymentLinkSubmit,
    paymentLinkType,
    setPaymentLinkType,
    discount,
    payableAmount,
    PendingAmt,
    formik,
    paymentLinkAmount,
    setPaymentLinkAmount,
    remark,
    setRemark
}) => {
    return (
        <Modal
            open={!!IsCollectModal?.orderId}
            closable={true}
            confirmLoading={Isloading}
            zIndex={1050}
            title={IsCollectModal?.additionalInfo?.customerName || IsCollectModal?.additionalInfo?.name || "Collect Payment"}
            style={{ top: 20 }}
            width={500}
            onOk={() => handlePaymentLinkSubmit()}
            okText={'Submit'}
            centered
            onCancel={() => {
                setCollectModal(null);
                setPaymentLinkType('Cash');
                setPaymentLinkAmount('');
                setRemark('');
            }}
            styles={{
                content: { borderRadius: '12px', overflow: 'hidden' }
            }}
        >
            <div>
                <div style={{ marginBottom: 20 }}>
                    <label style={{ fontWeight: 500, display: 'block', marginBottom: 8 }}>Payment Collection Method</label>
                    <Radio.Group
                        value={paymentLinkType}
                        onChange={e => setPaymentLinkType(e.target.value)}
                        style={{ width: '100%' }}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Radio value="Cash">Collect Cash</Radio>
                            <Radio value="Email Link">Send Payment Link via Email</Radio>
                            <Radio value="WhatsApp Link">Send Payment Link via WhatsApp</Radio>
                        </Space>
                    </Radio.Group>
                </div>

                {paymentLinkType === 'Cash' ? (
                    <>
                        <div
                            style={{
                                padding: '16px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '12px',
                                marginBottom: '20px',
                                border: '1px solid #e9ecef',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ color: '#6c757d' }}>Subtotal:</span>
                                <span style={{ fontWeight: '500' }}>₹{IsCollectModal?.amount}</span>
                            </div>
                            {discount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#198754' }}>
                                    <span>Discount:</span>
                                    <span>- ₹{discount}</span>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', paddingTop: '8px', borderTop: '1px dashed #dee2e6', fontWeight: '700' }}>
                                <span>Payable Amount:</span>
                                <span>₹{payableAmount}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#0d6efd', fontSize: '13px' }}>
                                <span>Already Paid:</span>
                                <span>₹{rowPaidAmount(IsCollectModal) || 0}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#d35400', fontSize: '13px', fontWeight: 600, marginTop: 4 }}>
                                <span>Pending:</span>
                                <span>₹{PendingAmt}</span>
                            </div>
                        </div>

                        <InputFromUser
                            FormGroupClassName={'mb-0'}
                            LabelClassName={'form-label'}
                            placeholder={'Enter Amount'}
                            FormControlClassName={'form-control'}
                            label={`Amount to Collect (Max: ₹${PendingAmt})`}
                            max={PendingAmt}
                            type={'number'}
                            name="amount"
                            error={formik?.errors?.updatedPaidAmount}
                            value={formik.values?.updatedPaidAmount}
                            touched={formik.touched.updatedPaidAmount}
                            onBlur={() => formik.setFieldTouched('updatedPaidAmount', true)}
                            onChange={e => {
                                const value = e?.target?.value;
                                if (value === '' || (Number(value) >= 0 && Number(value) <= PendingAmt)) {
                                    formik.setFieldValue('updatedPaidAmount', value);
                                }
                            }}
                        />
                    </>
                ) : (
                    <div style={{ padding: '16px', backgroundColor: '#f0f7ff', borderRadius: '8px', border: '1px solid #d6e4ff' }}>
                        <InputFromUser
                            FormGroupClassName={'mb-0'}
                            LabelClassName={'form-label'}
                            placeholder={'Enter Amount'}
                            FormControlClassName={'form-control'}
                            label={`Link Amount (Max: ₹${PendingAmt})`}
                            max={PendingAmt}
                            type={'number'}
                            name="paymentLinkAmount"
                            value={paymentLinkAmount}
                            onChange={e => {
                                const value = e?.target?.value;
                                if (value === '' || (Number(value) >= 0 && Number(value) <= PendingAmt)) {
                                    setPaymentLinkAmount(value);
                                }
                            }}
                        />

                        <div style={{ marginTop: 16 }}>
                            <p style={{ margin: '4px 0', fontSize: '13px' }}>
                                <strong>Customer:</strong> {IsCollectModal?.additionalInfo?.customerName || IsCollectModal?.additionalInfo?.name}
                            </p>
                            {paymentLinkType === 'Email Link' && (
                                <p style={{ margin: '4px 0', fontSize: '13px' }}>
                                    <strong>Email:</strong> {IsCollectModal?.additionalInfo?.email || 'Not available'}
                                </p>
                            )}
                            {paymentLinkType === 'WhatsApp Link' && (
                                <p style={{ margin: '4px 0', fontSize: '13px' }}>
                                    <strong>Phone:</strong> {IsCollectModal?.additionalInfo?.phoneNo || 'Not available'}
                                </p>
                            )}
                        </div>

                        <div style={{ marginTop: 12, fontSize: '12px', color: '#666', display: 'flex', gap: '8px' }}>
                            <InfoCircleOutlined />
                            <span>
                                {paymentLinkType === 'Email Link'
                                    ? 'A secure payment link will be sent to the customer\'s email.'
                                    : 'A secure payment link will be sent to the customer\'s WhatsApp.'}
                            </span>
                        </div>
                    </div>
                )}

                <div style={{ marginTop: 20 }}>
                    <InputFromUser
                        type="textarea"
                        label="Remark / Note"
                        placeholder="Enter any internal notes or remarks..."
                        name="remark"
                        value={remark}
                        onChange={e => setRemark(e.target.value)}
                        FormGroupClassName="mb-0"
                        LabelClassName="form-label"
                        FormControlClassName="form-control"
                    />
                </div>
            </div>
        </Modal>
    );
};

export default Checkout;
